import React from 'react';

import PropTypes from 'prop-types';
import { JsonParam, useQueryParam, withDefault } from 'use-query-params';
import { useParams } from 'react-router-dom';
import cn from 'classnames';

import Pagination from 'modules/common/components/Pagination';
import PageLoader from 'modules/common/components/PageLoader';
import EmptyState from 'modules/common/components/EmptyState';
import { Button } from 'modules/common/input';
import logo from 'assets/images/logo.svg';

import Categories from '../components/Categories';
import Grid from '../components/Grid';
import Row from '../components/Row';
import { DEFAULT_FILTER } from '../components/Filter';
import { useGetProducts, useGetCategories } from '../hooks';
import { useGetCart } from '../../../services/hooks';
import ProductSearch from '../components/ProductSearch';
import * as c from '../constants';

const DEFAULT_FORM = {
  layout: 'GRID',
  keyword: '',
};

function Order({ type, canProceed, branch }) {
  const [form, setForm] = React.useState(DEFAULT_FORM);
  const [showSearch, setShowSearch] = React.useState(false);

  const params = useParams();
  const [query, setQuery] = useQueryParam(
    'order',
    withDefault(JsonParam, DEFAULT_FILTER)
  );
  const [, , includes] = useGetCart();

  const { merchant_uuid, branch_uuid } = params || {};
  const hasSelectedBranch = canProceed(true, {
    withToast: false,
  })();

  const [isCategoryListLoading, categoryList] = useGetCategories({
    params: {
      level: 3,
      per_page: 30,
      branch_uuid: branch?.value,
    },
    merchant_uuid,
    branch_uuid: branch?.value,
    qrKey: params?.key,
    type,
  });
  const [isListLoading, list, pagination] = useGetProducts({
    params: {
      ...query,
      branch_uuid: branch?.value,
      is_available: 1,
    },
    merchant_uuid,
    branch_uuid,
    qrKey: params?.key,
    type,
  });

  const handleChangePage = (page) => {
    setQuery({
      ...query,
      page,
    });
  };

  const handleOnClick = (row) => () => {
    includes?.onShowOrderDetails(row, { type: 'CREATE' });
  };
  const layouts = () => {
    const args = {
      onClick: handleOnClick,
    };

    return {
      GRID: <Grid {...args} data={list} />,
      ROW: <Row {...args} data={list} />,
    };
  };

  const $categoryList = categoryList?.map((item) => ({
    label: item?.title,
    value: item?.category_uuid,
  }));

  const handleOnSearch = (v) => {
    setQuery({
      ...query,
      ...v(),
    });

    setForm((prevState) => ({
      ...prevState,
      ...v(),
    }));
  };

  // eslint-disable-next-line no-unused-vars
  const handleOnShowSearch = () => {
    setShowSearch(!showSearch);
  };

  React.useEffect(() => {
    setForm((prevState) => ({
      ...prevState,
      keyword: query?.keyword,
    }));
  }, [query?.keyword]);

  if (isCategoryListLoading) return <PageLoader />;

  return (
    <div className="flex flex-col flex-1 p-3 lg:px-10 lg:p-5 overflow-y-scroll md:overflow-y-auto overflow-x-hidden gap-3">
      {hasSelectedBranch && (
        <>
          <div className="flex flex-col md:flex-row gap-2 md:items-center">
            {!(type !== 'CUSTOMER') && (
              <div className="flex items-center gap-2 bg-white">
                <div
                  className={cn(
                    'flex flex-row items-end gap-2 flex-1 lg:!-mt-2'
                  )}
                >
                  <div>
                    <img
                      src={logo}
                      alt=""
                      className="h-6 lg:h-9 w-36 lg:w-40 object-contain"
                    />
                  </div>

                  <div className="font-extrabold lg:text-xl text-gray-800 text-sm leading-none md:leading-normal mb-1 md:mb-0">
                    DIGITAL MENU
                  </div>
                </div>
              </div>
            )}

            <div
              className={cn('overflow-hidden', {
                'md:ml-auto': !(type !== 'CUSTOMER'),
              })}
            >
              <div
                className={cn('transition-all duration-200', {
                  'mt-[-54px] md:mt-0': !showSearch,
                })}
              >
                <ProductSearch
                  onChange={setForm}
                  name="keyword"
                  value={form?.keyword}
                  placeholder={`Search in ${
                    query?.category?.label || 'All Products'
                  }`}
                  onSubmit={handleOnSearch}
                  enableSearchLabelInMobile
                  isLoading={isListLoading}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex flex-col overflow-x-auto w-full no-scrollbar">
              <Categories
                options={$categoryList}
                value={query?.category}
                name="category"
                onChange={setQuery}
              />
            </div>

            <div className="flex flex-shrink-0 items-center">
              {c.LAYOUT_OPTS?.map((item, key) => (
                <Button
                  // eslint-disable-next-line react/no-array-index-key
                  key={key}
                  label={<item.icon className="h-5 w-5" />}
                  className={cn(
                    'btn sm !py-1.5',
                    form?.layout === item?.value
                      ? 'primary'
                      : 'text-primary-500'
                  )}
                  onClick={() =>
                    setForm((prevState) => ({
                      ...prevState,
                      layout: item?.value,
                    }))
                  }
                />
              ))}
            </div>
          </div>

          {query?.keyword ? (
            <div className="text-sm">
              Searching{' '}
              <span className="font-semibold">
                &quot;{query?.keyword}&quot;
              </span>{' '}
              in{' '}
              <span className="font-semibold">
                {query?.category?.label || 'All Products'}
              </span>
              ...
            </div>
          ) : null}
        </>
      )}

      {(list || [])?.length > 0 && (
        <div
          className={cn(
            'mx-auto transition-all duration-100',
            isListLoading ? 'h-14 !-my-4' : 'h-0 !my-0'
          )}
        >
          <PageLoader className="!bg-transparent" width="100%" height="80px" />
        </div>
      )}

      <div
        className={cn('flex flex-col flex-1 sm:overflow-x-hidden p-2.5 -m-2', {
          'sm:overflow-y-scroll': hasSelectedBranch,
        })}
      >
        {(list || [])?.length > 0 && hasSelectedBranch ? (
          layouts()?.[form?.layout]
        ) : (
          <>
            {isListLoading ? (
              <PageLoader className="!bg-transparent" />
            ) : (
              <EmptyState
                contentClassName="!space-y-0"
                containerClassName="!bg-transparent"
                {...(!hasSelectedBranch && {
                  label: 'No branch selected',
                  note: 'To proceed, select a branch first.',
                })}
              />
            )}
          </>
        )}
      </div>

      <div className="pt-2">
        <Pagination
          onChange={handleChangePage}
          value={pagination}
          small
          isLoading={isListLoading}
        />
      </div>
    </div>
  );
}

Order.defaultProps = {
  type: 'BRANCH',
  canProceed:
    (event) =>
    (...args) =>
      typeof event === 'function' ? event(args) : event,
  branch: {},
};

Order.propTypes = {
  type: PropTypes.string,
  canProceed: PropTypes.instanceOf(Function),
  branch: PropTypes.instanceOf(Object),
};

export default Order;
